"use client";

import { useFieldArray, useForm } from "react-hook-form";

import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
} from "@stocks/ui";
import type { ProductDetailResponse } from "@stocks/shared";

import { useCreateProduct } from "../model/useCreateProduct";

interface Values {
  name: string;
  description: string;
  options: { color: string; size: string }[];
}

const EMPTY: Values = { name: "", description: "", options: [] };

/** 생산 제품 등록 (색상/사이즈 옵션을 함께 등록할 수 있다) */
export const CreateProductForm = ({
  onCreated,
}: {
  onCreated?: (product: ProductDetailResponse) => void;
}) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({ defaultValues: EMPTY });
  const { fields, append, remove } = useFieldArray({ control, name: "options" });
  const create = useCreateProduct();

  const onSubmit = async (v: Values) => {
    const res = await create.mutateAsync({
      name: v.name.trim(),
      description: v.description.trim() || undefined,
      options: v.options
        .map((o) => ({ color: o.color.trim(), size: o.size.trim() }))
        .filter((o) => o.color && o.size),
    });
    reset(EMPTY);
    if (res.data.data) onCreated?.(res.data.data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
      <div className='grid gap-4 sm:grid-cols-2'>
        <Field>
          <FieldLabel htmlFor='product-name'>제품명</FieldLabel>
          <Input
            id='product-name'
            {...register("name", { required: "제품명을 입력하세요." })}
          />
          {errors.name && <FieldError>{errors.name.message}</FieldError>}
        </Field>
        <Field>
          <FieldLabel htmlFor='product-description'>설명</FieldLabel>
          <Input id='product-description' {...register("description")} />
        </Field>
      </div>

      <div className='flex flex-col gap-2'>
        <span className='text-sm font-medium'>색상 · 사이즈 옵션</span>
        {fields.map((f, i) => (
          <div key={f.id} className='flex flex-wrap items-center gap-2'>
            <Input
              className='w-40'
              aria-label={`옵션 ${i + 1} 색상`}
              placeholder='색상'
              {...register(`options.${i}.color`)}
            />
            <Input
              className='w-32'
              aria-label={`옵션 ${i + 1} 사이즈`}
              placeholder='사이즈'
              {...register(`options.${i}.size`)}
            />
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => remove(i)}
            >
              삭제
            </Button>
          </div>
        ))}
        <div>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => append({ color: "", size: "" })}
          >
            옵션 추가
          </Button>
        </div>
      </div>

      <div>
        <Button type='submit' disabled={create.isPending}>
          {create.isPending ? "등록 중…" : "제품 등록"}
        </Button>
      </div>
    </form>
  );
};
